import { apiClient } from './apiClient';

export type ChatMetadata = {
    id: string;
    name: string;
}

type ChatMetadatasResponse = {
    chatMetadatas: ChatMetadata[];
    count: number;
    next: string | null;
    previous: string | null;
}

export async function getChatMetadatasForProject(projectId: string, url?: string): Promise<ChatMetadatasResponse> {
    if (url === undefined) {
        url = `/api/projects/${projectId}/chats/`;
    }

    const resolvedUrl = url.startsWith('http') ? url : `${import.meta.env.VITE_API_URL}${url}`;
    const response = await apiClient.get(resolvedUrl);
    return {
        chatMetadatas: response.data.results,
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
    }
}

type ProjectChatMetadataMap = Record<string, ChatMetadatasResponse>;

export async function batchGetChatMetadatasForProjects(projectIds: string[]): Promise<ProjectChatMetadataMap> {
    const projectChatMetadataList = await Promise.all(
        projectIds.map(async projectId => {
            const response = await getChatMetadatasForProject(projectId);
            return { projectId, response }
        })
    );

    return projectChatMetadataList.reduce<ProjectChatMetadataMap>(
        (acc, { projectId, response }) => {
            acc[projectId] = response;
            return acc;
        }, 
        {}
    );
}

export async function createChat(
    projectId: string | null,
    name: string | null,
    first_message: string = ""): Promise<ChatMetadata> {

    let url: string;
    let body: { name?: string; first_message?: string };

    if (projectId) {
        url = `${import.meta.env.VITE_API_URL}/api/projects/${projectId}/chats/`;
        if (first_message.trim()) {
            body = { first_message: first_message.trim() };
        } else {
            body = { name: name?.trim() || 'New chat' };
        }
    } else {
        // Create projectless chat via /api/chats/
        url = `${import.meta.env.VITE_API_URL}/api/chats/`;
        if (first_message.trim()) {
            body = { first_message: first_message.trim() };
        } else {
            body = { name: name?.trim() || 'New chat' };
        }
    }

    const response = await apiClient.post(url, body);
    return response.data;
}

export async function getProjectlessChats(url?: string): Promise<ChatMetadatasResponse> {
    const resolvedUrl = url
        ? (url.startsWith('http') ? url : `${import.meta.env.VITE_API_URL}${url}`)
        : `${import.meta.env.VITE_API_URL}/api/chats/?has_project=false`;

    const response = await apiClient.get(resolvedUrl);
    return {
        chatMetadatas: response.data.results,
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
    };
}
